import { DifficultyLevel } from '../interfaces/question.interface';
import { FractionRound } from '../interfaces/fraction-line.interface';

/**
 * Fraction pool for Fraction Blast — interior fractions on 0–1 with a
 * spread of denominators (2 → 10). Grade-4 fraction sense: no tick
 * marks on the line, so the child estimates the magnitude.
 */
const POOL: { n: number; d: number }[] = [
  { n: 1, d: 2 },
  { n: 1, d: 3 }, { n: 2, d: 3 },
  { n: 1, d: 4 }, { n: 3, d: 4 },
  { n: 1, d: 5 }, { n: 2, d: 5 }, { n: 3, d: 5 }, { n: 4, d: 5 },
  { n: 1, d: 6 }, { n: 5, d: 6 },
  { n: 1, d: 8 }, { n: 3, d: 8 }, { n: 5, d: 8 }, { n: 7, d: 8 },
  { n: 1, d: 10 }, { n: 3, d: 10 }, { n: 7, d: 10 }, { n: 9, d: 10 }
];

/** Accept band: 70% of a half-step (0.5/d), floored so it's never
 *  impossibly tight. Tighter for larger denominators — a real estimate. */
function toleranceFor(d: number): number {
  return Math.max(0.03, 0.35 / d);
}

function difficultyFor(d: number): DifficultyLevel {
  if (d <= 3) return DifficultyLevel.EASY;
  if (d <= 5) return DifficultyLevel.MEDIUM;
  return DifficultyLevel.HARD;
}

function toRound(p: { n: number; d: number }, idx: number): FractionRound {
  return {
    id: `frac-${p.n}-${p.d}-${idx}`,
    numerator: p.n,
    denominator: p.d,
    value: p.n / p.d,
    tolerance: toleranceFor(p.d),
    difficulty: difficultyFor(p.d)
  };
}

/**
 * Build a session's rounds: sort the pool into an easy→hard gradient
 * (shuffled within each denominator band) and sample `count` evenly
 * across it, so every session ramps from a friendly half/third up to a
 * tight tenth, with a fresh mix each time.
 */
export function buildFractionLineRounds(count: number): FractionRound[] {
  const gradient = shuffleBands(POOL);
  const n = gradient.length;
  const k = Math.min(count, n);
  if (k <= 0) return [];
  if (k === 1) return [toRound(gradient[0], 0)];

  const used = new Set<number>();
  const out: FractionRound[] = [];
  for (let i = 0; i < k; i++) {
    let idx = Math.round((i * (n - 1)) / (k - 1));
    while (used.has(idx)) idx = (idx + 1) % n;
    used.add(idx);
    out.push(toRound(gradient[idx], i));
  }
  return out;
}

/** Ascending by denominator, shuffled within each denominator band. */
function shuffleBands(pool: { n: number; d: number }[]): { n: number; d: number }[] {
  const bands = new Map<number, { n: number; d: number }[]>();
  for (const p of pool) {
    const band = bands.get(p.d) ?? [];
    band.push(p);
    bands.set(p.d, band);
  }
  const out: { n: number; d: number }[] = [];
  [...bands.keys()].sort((a, b) => a - b).forEach(d => {
    const band = bands.get(d)!;
    for (let i = band.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [band[i], band[j]] = [band[j], band[i]];
    }
    out.push(...band);
  });
  return out;
}
